import { useCallback } from 'react';
import { useSearchAlertInfo } from '@pkgs/components/hooks/useSearchAlertInfo';
import { useLipaPortfolioTrackButtonClick } from '@pkgs/components/hooks/useLipaPortfolioTrackButtonClick';
import { useIsLipaPage } from '@pkgs/components/hooks/useIsLipaPage';
import { useButtonIconVisibility } from '@pkgs/components/hooks/useButtonIconVisibility';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useMapStore } from '@pkgs/components/hooks/stores/useMapStore';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { defaultAdvancedFiltersState } from '@pkgs/components/hooks/stores/search/useAdvancedFiltersModified';
import { defaultPreferencesState } from '@pkgs/components/hooks/stores/search/slices/preferencesSlice';
import { DEFAULT_SORTING_OPTION_ID } from '@pkgs/components/hooks/stores/search/slices/optionsSlice';
import { usePlaceDetails } from '@pkgs/components/hooks/queries/usePlaceDetails';
import { SortingOptionId } from '@pkgs/components/enums/enums';
import User from '@pkgs/components/assets/icons/small/User.svg';
import Refresh from '@pkgs/components/assets/icons/small/Refresh.svg';
import { Button, type ButtonProps } from '@ev-tech/ui-core-components/Button';
import { UserActionEnum } from '@ev-tech/snowplow-library';
import { replaceLocaleInPath } from '@ev-tech/search-modules-api';

import { SearchGlobalButton } from './SearchGlobalButton';
import { SearchAlertButtonComponent } from '../SearchAlert/SearchAlertButton';
import { handleSearchGlobalButtonClick } from '../../utils/searchResetUtils';

import styles from './NoResultsBanner.module.css';

export const NoResultBannerActionButtons = ({ hasResettableFilters }: { hasResettableFilters: boolean }) => {
  const isPrivateOffice = useSearchStore((state) => state.privateOffice);
  const setSearchRadius = useSearchStore((state) => state.setSearchRadius);
  const isLipaPage = useIsLipaPage();
  const { buttonRef, shouldShowIcon } = useButtonIconVisibility({ heightOfOneLine: 48 });
  const { t, countryCode, options, featureFlags } = useAppContext();
  const { language } = options;
  const { combinedBoundingBox } = usePlaceDetails();

  const resetState = useSearchStore((state) => state.resetState);
  const getStateSnapshot = useSearchStore((state) => state.getStateSnapshot);

  const resetShopState = useSearchStore((state) => state.resetShopState);
  const updateMapBounds = useMapStore((state) => state.updateMapBounds);
  const setShopIds = useSearchStore((state) => state.setShopIds);
  const setMasterDataShopIds = useSearchStore((state) => state.setMasterDataShopIds);

  const contactLinkUrl = replaceLocaleInPath(t('search.results.noResults.contactLink'), language, countryCode);

  const { trackButtonClick: trackResetAllFiltersButtonClick } = useLipaPortfolioTrackButtonClick({
    elementId: 'property-no-result_reset-filter-primary',
    action: UserActionEnum.click,
  });

  const { trackButtonClick: trackSearchGlobalNetworkButtonClick } = useLipaPortfolioTrackButtonClick({
    elementId: 'property-no-result_search-all-tertiary',
    action: UserActionEnum.click,
  });

  const handleResetClick = () => {
    trackResetAllFiltersButtonClick();
    resetState({
      ...defaultAdvancedFiltersState,
      ...defaultPreferencesState,
      sortingOptionId: featureFlags.isSmartSortEnabled ? SortingOptionId.SMART_SORT : DEFAULT_SORTING_OPTION_ID,
    });
  };

  const onSearchGlobalButtonClick = useCallback(() => {
    handleSearchGlobalButtonClick({
      searchState: getStateSnapshot(),
      isLipaPage,
      isPrivateOffice,
      options,
      combinedBoundingBox,
      resetShopState,
      setMasterDataShopIds,
      setSearchRadius,
      setShopIds,
      trackSearchGlobalNetworkButtonClick,
      updateMapBounds,
    });
  }, [
    isPrivateOffice,
    isLipaPage,
    trackSearchGlobalNetworkButtonClick,
    setShopIds,
    setMasterDataShopIds,
    getStateSnapshot,
    options,
    resetShopState,
    updateMapBounds,
    combinedBoundingBox,
    setSearchRadius,
  ]);

  const [_, setShouldShowSearchAlertInfo] = useSearchAlertInfo();

  const { trackButtonClick: trackContactAdvisorButtonClick } = useLipaPortfolioTrackButtonClick({
    elementId: 'property-no-result_contact-advisor-link',
    targetUrl: contactLinkUrl.toString(),
    action: UserActionEnum.click,
  });

  const contactButtonProps: ButtonProps = {
    label: t('search.results.noResults.button.contactAgent'),
    icon: <User />,
    as: 'button',
    onClick: (e) => {
      e.preventDefault();
      trackContactAdvisorButtonClick();
      window.open(contactLinkUrl, '_self');
    },
    testId: 'search-components_no-results-banner_contact-button',
  };

  const resetButtonProps: ButtonProps = {
    label: t('search.results.noResults.button.resetAllFilters'),
    icon: <Refresh />,
    onClick: handleResetClick,
    as: 'button',
    testId: 'search-components_no-results-banner_reset-button',
  };

  const lipaPoResettableFiltersButtons = () => (
    <>
      <SearchGlobalButton
        key="searchGlobal"
        variant="tertiary"
        onClick={onSearchGlobalButtonClick}
        shouldShowIcon={shouldShowIcon}
        buttonRef={buttonRef}
        buttonLabel={isPrivateOffice ? t('search.privateOffice.results.noResults.buttonText') : t('search.licencePartner.ctaCard.btn')}
        dataTestId="search-components_no-results-banner_search-global-network-button"
      />
      <Button key="reset" {...resetButtonProps} />
    </>
  );

  const lipaPoNoResettableFiltersButtons = () => (
    <SearchGlobalButton
      key="searchGlobal"
      onClick={onSearchGlobalButtonClick}
      shouldShowIcon={shouldShowIcon}
      buttonRef={buttonRef}
      buttonLabel={isPrivateOffice ? t('search.privateOffice.results.noResults.buttonText') : t('search.licencePartner.ctaCard.btn')}
      dataTestId="search-components_no-results-banner_search-global-network-button"
    />
  );

  const resettableFiltersButtons = () => (
    <>
      <SearchAlertButtonComponent
        key="searchAlert"
        dataTestId="search-components_no-results-banner_search-alert-button"
        setShouldShowSearchAlertInfo={setShouldShowSearchAlertInfo}
        variant="tertiary"
        size="medium"
        tooltipPlacement="bottom"
      />
      <Button key="reset" {...resetButtonProps} />
    </>
  );

  const noResettableFiltersButtons = () => (
    <>
      <Button variant="tertiary" {...contactButtonProps} />
      <SearchAlertButtonComponent
        key="searchAlert"
        dataTestId="search-components_no-results-banner_search-alert-button"
        setShouldShowSearchAlertInfo={setShouldShowSearchAlertInfo}
        size="medium"
        tooltipPlacement="bottom"
      />
    </>
  );

  const getButtons = () => {
    switch (true) {
      case (isPrivateOffice || isLipaPage) && hasResettableFilters:
        return lipaPoResettableFiltersButtons();
      case isPrivateOffice || isLipaPage:
        return lipaPoNoResettableFiltersButtons();
      case hasResettableFilters:
        return resettableFiltersButtons();
      default:
        return noResettableFiltersButtons();
    }
  };
  return <div className={styles.buttonContainer}>{getButtons()}</div>;
};
